import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { getPaymentStatus } from "@/lib/api";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Download, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | failed
  const [downloads, setDownloads] = useState([]);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let attempts = 0;
    let active = true;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await getPaymentStatus(sessionId);
        if (!active) return;
        if (res.payment_status === "paid") {
          setDownloads(res.downloads || []);
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(res.payment_status)) { setState("failed"); return; }
      } catch { /* keep trying */ }
      if (attempts >= 12) { setState("failed"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-2xl flex-col items-center justify-center px-5 py-24 text-center" data-testid="payment-success-page">
      {state === "polling" && (<><Loader2 className="h-10 w-10 animate-spin" /><h1 className="mt-6 font-display text-3xl tracking-tight">Confirming your payment…</h1><p className="mt-2 text-muted-foreground">This only takes a moment.</p></>)}
      {state === "paid" && (
        <>
          <CheckCircle2 className="h-12 w-12" />
          <h1 className="mt-6 font-display text-4xl font-extrabold tracking-tight">Thank you. You're in.</h1>
          <p className="mt-3 text-muted-foreground">Your guides are ready to download below — and we've emailed them to you for lifetime access.</p>
          <div className="mt-10 w-full space-y-3" data-testid="download-list">
            {downloads.map((d, i) => (
              <a key={i} href={`${API}/download/${d.url.split("/download/")[1]}`} data-testid={`download-link-${i}`}
                className="flex items-center justify-between border border-black/10 px-6 py-4 text-left transition-colors hover:bg-muted">
                <span className="font-display text-lg tracking-tight">{d.title}</span>
                <span className="inline-flex items-center gap-2 text-sm"><Download className="h-4 w-4" /> Download</span>
              </a>
            ))}
          </div>
          <Link to="/shop" className="mt-10"><Button variant="outline" className="rounded-none h-11 border-black/20 px-8">Continue shopping</Button></Link>
        </>
      )}
      {state === "failed" && (
        <>
          <XCircle className="h-12 w-12" />
          <h1 className="mt-6 font-display text-3xl tracking-tight">We couldn't confirm your payment.</h1>
          <p className="mt-2 text-muted-foreground">If you were charged, contact us and we'll sort it out immediately.</p>
          <div className="mt-8 flex gap-3">
            <Link to="/shop"><Button variant="outline" className="rounded-none h-11 border-black/20 px-8">Back to shop</Button></Link>
            <Link to="/contact"><Button className="rounded-none h-11 bg-primary px-8 text-primary-foreground">Contact support</Button></Link>
          </div>
        </>
      )}
    </div>
  );
}
